import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getTotalPageItems, paginate } from "../../../utils/paginationUtil";
import styles from "./badgesList.module.scss";
import { gaColors } from "../../../assets/globalConstants";
import { Button, CloseIcon, HeadingContainer } from "@gataca/design-system/web";
import CardListSkeleton from "../../../components/skeletons/cardListSkeleton/CardListSkeleton";
import SearchableListHeader from "../../../components/SearchableListHeader/SearchableListHeader";
import {
  headersBadgesListTable,
  optionsFilter,
} from "./data/BadgesList.constants";
import BadgesTable from "./table/BadgesTable";
import TableSkeleton from "../../../components/skeletons/tableSkeleton/TableSkeleton";
import { defaultPageSize } from "../../../components/Pagination/paginationConstants";
import { ActionStatusTypes, IBadgesTableData } from "../../../types/interfaces";
import {
  badgesData,
  createBadgesResponse,
  createBadgesStatus,
  deleteBadgeStatus,
  loadBadgesDataStatus,
  selectedBadgeId,
} from "../../../store/badges/badgesSelectors";
import {
  createBadges,
  deleteBadge,
  getBadgesData,
  selectBadgeId,
} from "../../../store/badges/badgesActions";
import Dialog from "../../../components/modals/dialog/Dialog";
import { setSelectedRoute } from "../../../store/navigation/navigationActions";
import { showScreenLoaderAction } from "../../../store/loader/loaderActions";
import FullScreenLoader from "../../../components/elements/screenLoader/ScreenLoader";
import { screenLoaderActive } from "../../../store/loader/loaderSelectors";
import CreateModal from "./createModal/CreateModal";
import {
  getAllowedScopes,
  getSessionRead,
  getTokenExpiration,
} from "../../../store/session/sessionSelectors";
import { deleteSession } from "../../../store/session/sessionActions";
import { history } from "../../../services/history";
import {
  getIsActiveSession,
  userHasScopes,
} from "../../../utils/permissionsUtil";
import Notification from "../../../components/Notification/Notification";
import { useTranslation } from "react-i18next";

type IBadgesListProps = {};

export type ICreationFormState = {
  campaignName?: string;
  file?: File;
};

const BadgesList: React.FC<IBadgesListProps> = ({}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Selectors
  const getDataStatus = useSelector(loadBadgesDataStatus);
  const dataBadgesList = useSelector(badgesData)?.badges;
  const badgeId = useSelector(selectedBadgeId);
  const showScreenLoader = useSelector(screenLoaderActive);
  const creationResponse = useSelector(createBadgesResponse);
  const creationStatus = useSelector(createBadgesStatus);
  const deleteStatus = useSelector(deleteBadgeStatus);
  const tokenExpiration = useSelector(getTokenExpiration);
  const sessionRead = useSelector(getSessionRead);
  const availableScopes = useSelector(getAllowedScopes);
  const dataBadgesData = useSelector(badgesData)?.data;

  // States and constants

  const [searchValue, setSearchValue] = React.useState<string>("");
  const [searchValueMessage, setSearchValueMessage] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [paginatedBadgesList, setPaginatedBadgesList] =
    useState<IBadgesTableData[]>();
  const [dataBadges, setDataBadges] = useState<IBadgesTableData[]>();

  const [totalItems, setTotalItems] = useState(dataBadgesData?.total || 0);

  const [showDeleteWarning, setShowDeleteModal] = useState(false);
  const [showCreateBadgesModal, setShowCreateBadgesModal] = useState(false);
  const [showCloseCreationModal, setShowCloseCreationModal] = useState(false);
  const [showErrorCreationModal, setShowErrorCreationModal] = useState(false);
  const [resultsOnLoad, setResultsOnLoad] = useState(false);
  const [searchValueOnEnter, setSearchValueOnEnter] =
    React.useState<string>("");
  const initialFormState: ICreationFormState = {
    campaignName: "",
    file: undefined,
  };

  const [form, setFormState] = useState(initialFormState);
  const { campaignName, file } = form;
  const hasReadDeleteAndCreateScope = !!(
    availableScopes && userHasScopes(["authenticated"], availableScopes)
  );
  const canOperate =
    hasReadDeleteAndCreateScope && getIsActiveSession(tokenExpiration);

  const valueDisplay = optionsFilter[0];
  const [selectedOption, setSelectedOption] = useState(valueDisplay);
  const selectedValueRefactor = selectedOption?.value?.toLowerCase();
  const noResults = !(dataBadgesList && dataBadgesList?.length > 0);

  // Functions
  const showDeleteModal = (itemId) => {
    dispatch(selectBadgeId(itemId));
    setShowDeleteModal(true);
  };

  const hideDeleteModal = () => {
    dispatch(selectBadgeId(undefined));
    setShowDeleteModal(false);
  };

  const handlePageChange = (page) => {
    dispatch(
      getBadgesData(selectedValueRefactor, searchValue, page, defaultPageSize)
    );
    setPageIndex(page);
    setPaginatedBadgesList(paginate(dataBadges, defaultPageSize, page));
  };

  const handleSearchedValidation = (event) => {
    if (event.key === "Enter") {
      dispatch(
        getBadgesData(selectedValueRefactor, searchValue, 1, defaultPageSize)
      );
      setPageIndex(1);
      if (searchValue != "") {
        setSearchValueMessage(true);
      }
      setSearchValueOnEnter(searchValue);
    }
  };

  const cleanSearchBar = () => {
    setSearchValue("");
    dispatch(getBadgesData(selectedValueRefactor, "", 1, defaultPageSize));
    setPageIndex(1);
    setSearchValueMessage(false);
  };

  const searchValueOnSelectorChange = (item) => {
    if (searchValue != "") {
      dispatch(getBadgesData(item?.value, searchValue, 1, defaultPageSize));
      setPageIndex(1);
      if (dataBadgesList && dataBadgesList?.length > 0) {
        setSearchValueMessage(true);
      }
    }
  };

  const deleteSelectedBadge = () => {
    badgeId && dispatch(showScreenLoaderAction());
    badgeId && dispatch(deleteBadge(badgeId));
    hideDeleteModal();
  };

  const createFunction = () => {
    if (campaignName && file) {
      dispatch(showScreenLoaderAction());
      setShowCloseCreationModal(false);
      setShowCreateBadgesModal(false);
      dispatch(createBadges(campaignName, file));
    }
  };

  const displayCloseCreationModal = () => {
    if (campaignName?.trim()?.length || !!file) {
      setShowCloseCreationModal(true);
      setShowCreateBadgesModal(false);
    } else {
      hideCreationModal();
    }
  };

  const hideCloseCreationModal = () => {
    setShowCloseCreationModal(false);
    setShowCreateBadgesModal(true);
  };

  const hideCreationModal = () => {
    setShowCloseCreationModal(false);
    setShowCreateBadgesModal(false);
    setFormState(initialFormState);
  };

  const hideErrorCreationModal = () => {
    setShowErrorCreationModal(false);
    setFormState(initialFormState);
  };

  const showCreationModalAfterError = () => {
    setShowErrorCreationModal(false);
    setShowCreateBadgesModal(true);
    setFormState({ ...form, file: undefined });
  };

  const goToLoginAndDeleteSession = () => {
    dispatch(setSelectedRoute("login"));
    dispatch(deleteSession());
    history.push("/login");
  };

  // useEffect

  useEffect(() => {
    if (sessionRead) {
      if (!!tokenExpiration) {
        dispatch(setSelectedRoute(`/badges`));
        if (!!canOperate) {
          dispatch(
            getBadgesData(
              selectedValueRefactor,
              searchValue,
              1,
              defaultPageSize
            )
          );
        }
      } else {
        goToLoginAndDeleteSession();
      }
    }
  }, [sessionRead, tokenExpiration, availableScopes, canOperate]);

  useEffect(() => {
    setDataBadges(dataBadgesList);
    if (dataBadgesList && dataBadgesList?.length > 0) {
      setResultsOnLoad(true);
    }
  }, [dataBadgesList, defaultPageSize, pageIndex]);

  useEffect(() => {
    if (!!dataBadges) {
      setPaginatedBadgesList(paginate(dataBadges, defaultPageSize, pageIndex));
    }
  }, [dataBadges]);

  useEffect(() => {}, [paginatedBadgesList]);

  useEffect(() => {
    setTotalItems(dataBadgesData?.total || 0);
  }, [dataBadgesData]);

  useEffect(() => {
    if (creationResponse) {
      const { badgesCreated, validationErrors, creationError } =
        creationResponse;
      if (
        creationStatus === ActionStatusTypes.success ||
        badgesCreated?.length
      ) {
        cleanSearchBar();
      }
      if (validationErrors?.length) {
        validationErrors?.length && setShowErrorCreationModal(true);
      } else {
        setFormState(initialFormState);
      }
    }
  }, [creationResponse]);

  useEffect(() => {
    if (creationStatus && creationStatus === ActionStatusTypes.failed) {
      setFormState(initialFormState);
    }
  }, [creationStatus]);

  useEffect(() => {
    if (deleteStatus === ActionStatusTypes.success) {
      cleanSearchBar();
    }
  }, [deleteStatus]);

  const getValidationErrorListParams = () => {
    return creationResponse?.validationErrors?.map((error) => {
      const [rowPart, ...rest] = error.split(".");
      const title = rowPart.replace("Row: ", "Row ").trim();
      const content = rest.join(".").trim();
      return {
        type: "ghost",
        condition: "all",
        extraInfo: content,
        leading: "icon",
        leadingIcon: {
          Icon: CloseIcon,
          color: gaColors.alert300,
        },
        className: styles.listItem,
        title: title,
      };
    });
  };

  const paginationData = {
    currentPage: pageIndex,
    totalPages: getTotalPageItems(totalItems || 0, defaultPageSize) || 0,
    onChange: handlePageChange,
    totalItems: totalItems || 0,
    pageSize: defaultPageSize,
    page: pageIndex,
    className: "marginTop32",
  };

  return (
    <>
      <Notification />
      <div className={styles?.headerContainer}>
        <HeadingContainer
          size="medium"
          titleText={t("badgesList.headerTitle")}
          contentText={t("badgesList.headerContentText")}
          contentTextColor={"grey"}
        />

        {canOperate ? (
          <Button
            text={t("badgesList.buttonText")}
            style="fill"
            color="purple"
            textSize="medium"
            onClick={() => setShowCreateBadgesModal(true)}
          />
        ) : null}
      </div>
      {getDataStatus != ActionStatusTypes?.loading ? (
        <>
          {resultsOnLoad ? (
            <SearchableListHeader
              className={styles.searchBar}
              handleSearchedChange={(e) => setSearchValue(e?.target?.value)}
              cleanSearchBar={cleanSearchBar}
              optionsFilter={optionsFilter}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              searchValue={searchValue}
              handleSearchedValidation={handleSearchedValidation}
              searchValueOnSelectorChange={searchValueOnSelectorChange}
            />
          ) : null}
        </>
      ) : (
        <CardListSkeleton
          cardsNumber={1}
          className={styles.searchBarSkeleton}
        />
      )}
      {getDataStatus != ActionStatusTypes?.loading ? (
        <>
          <BadgesTable
            tableData={dataBadgesList}
            headerData={headersBadgesListTable}
            paginationData={paginationData}
            deleteItem={showDeleteModal}
            searchValue={searchValue}
            searchValueMessage={searchValueMessage}
            canDelete={!!canOperate}
            noResults={noResults}
            resultsOnLoad={resultsOnLoad}
            searchValueOnEnter={searchValueOnEnter}
          />

          {showDeleteWarning ? (
            <Dialog
              use="actionable"
              title={t("badgesList.deleteModalTitle")}
              contentText={t("badgesList.deleteModalDesc")}
              closableByBg={false}
              onClose={hideDeleteModal}
              primaryButton={{
                onClick: deleteSelectedBadge,
                showText: true,
                state: "enable",
                color: "red",
                textSize: "medium",
                text: t("public.delete"),
              }}
              secondaryButton={{
                onClick: hideDeleteModal,
                showText: true,
                state: "enable",
                textSize: "medium",
                text: t("public.cancel"),
              }}
            />
          ) : null}
        </>
      ) : (
        <TableSkeleton
          columnsNumber={5}
          rowsNumber={defaultPageSize}
          responsiveCards={true}
        />
      )}

      {showCloseCreationModal ? (
        <Dialog
          use="actionable"
          title={t("badgesList.closeCreationDialog.title")}
          contentText={t("badgesList.closeCreationDialog.description")}
          closableByBg={false}
          onClose={hideCloseCreationModal}
          primaryButton={{
            onClick: hideCreationModal,
            showText: true,
            state: "enable",
            color: "red",
            textSize: "medium",
            text: t("public.close"),
          }}
          secondaryButton={{
            onClick: hideCloseCreationModal,
            showText: true,
            state: "enable",
            textSize: "medium",
            text: t("public.cancel"),
          }}
        />
      ) : null}

      {showCreateBadgesModal ? (
        <CreateModal
          hideModal={displayCloseCreationModal}
          uploadFile={createFunction}
          form={form}
          setFormState={setFormState}
        />
      ) : null}

      {showErrorCreationModal ? (
        <Dialog
          use="status"
          status="warning"
          title={t("badgesList.creationErrorModal.title")}
          contentText={t("badgesList.creationErrorModal.description")}
          closableByBg={false}
          onClose={hideErrorCreationModal}
          primaryButton={{
            onClick: hideErrorCreationModal,
            showText: true,
            state: "enable",
            color: "purple",
            textSize: "medium",
            text: t("public.ok"),
          }}
          list={{
            bgColor: "grey",
            list: getValidationErrorListParams(),
          }}
          secondaryButton={{
            onClick: showCreationModalAfterError,
            showText: true,
            state: "enable",
            color: "grey",
            textSize: "medium",
            text: t("badgesList.creationErrorModal.addToCampaignButton"),
          }}
        />
      ) : null}

      {showScreenLoader && <FullScreenLoader />}
    </>
  );
};

export default BadgesList;
